import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CartModel from '../../../../models/CartModel';
import NoActivityPlaceholder from './NoActivityPlaceholder';
import { formatSummaryDateWithSeconds } from '../../../../../util/dateUtils';
import ButtonComponent from '../../../../components/ButtonMC';
import LoadingMC from '../../../../components/LoadingMC';
import IconMC from '../../../../components/IconMC';
import { cartTotal, formatCartPrice } from './cartFormat';

const ITEMS_PER_PAGE = 20;

const parseHistoryResponse = (response) => {
  const data = Array.isArray(response)
    ? response
    : (Array.isArray(response?.data) ? response.data : []);
  const total = Array.isArray(response)
    ? response.length
    : Number(response?.total ?? response?.meta?.total ?? data.length);

  return { data, total: Number.isFinite(total) ? total : data.length };
};

const CartHistory = ({ refreshKey, customerId }) => {
  const { t } = useTranslation('ChatbotIA');
  const { customer } = useSelector((reduxState) => reduxState.mainReducer);
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const currency = retailer_info?.currency_code ? `${retailer_info.currency_code} ` : '';
  const customerIdToUse = customerId !== undefined ? customerId : customer?.id;
  const [carts, setCarts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(Boolean(customerIdToUse));
  const hasNextPage = carts.length < total;

  useEffect(() => {
    if (!customerIdToUse) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    CartModel.listHistory(String(customerIdToUse), {
      limit: ITEMS_PER_PAGE,
      skip: 0
    })
      .then((response) => {
        if (cancelled) return;
        const parsed = parseHistoryResponse(response);
        setCarts(parsed.data);
        setTotal(parsed.total);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching cart history', error);
        setCarts([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey, customerIdToUse]);

  const loadMoreCarts = useCallback(() => {
    if (!customerIdToUse || loading || !hasNextPage) {
      return;
    }

    setLoading(true);
    CartModel.listHistory(String(customerIdToUse), {
      limit: ITEMS_PER_PAGE,
      skip: carts.length
    })
      .then((response) => {
        const parsed = parseHistoryResponse(response);
        setCarts((prev) => [...prev, ...parsed.data]);
        setTotal(parsed.total);
      })
      .catch((error) => {
        console.error('Error fetching cart history', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [customerIdToUse, carts.length, hasNextPage, loading]);

  if (!loading && carts.length === 0) {
    return <NoActivityPlaceholder />;
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-4">
      <LoadingMC loading={loading && carts.length === 0} />
      <ul className="p-0 tw-m-0 tw-flex-shrink-0">
        {carts.map((historyCart, index) => {
          const historyItems = historyCart.items || [];

          return (
            <li className="d-flex pb-24" key={historyCart.id}>
              <div className="d-flex justify-content-center position-relative w-32">
                <div className="d-flex h-32 justify-content-center w-32 tw-items-center tw-rounded-full tw-bg-gray-50">
                  <IconMC name="shopping-cart-outline" size="base" className="tw-text-blue-500" />
                </div>
                {index !== carts.length - 1 && (
                  <div className="h-perc-99 mt-32 position-absolute w-2" style={{ backgroundColor: 'lightblue' }} />
                )}
              </div>

              <div className="d-flex flex flex-column pl-8 tw-min-w-0">
                <span className="fz-12 mb-4 tw-font-medium tw-text-gray-950">
                  {t(`cart.inbox.status.${historyCart.status}`, { defaultValue: historyCart.status })}
                  {' · '}
                  {t('cart.inbox.items_count', { count: historyItems.length })}
                  {' · '}
                  {formatCartPrice(currency, cartTotal(historyItems))}
                </span>
                {historyItems.map((item, itemIndex) => (
                  <span
                    className="fz-11 tw-text-gray-500 tw-truncate"
                    key={`${historyCart.id}-${item.product_id}-${item.variant_id || 'none'}-${itemIndex}`}
                  >
                    {item.name}
                    {item.variant_name ? ` · ${item.variant_name}` : ''}
                    {' × '}
                    {item.quantity}
                  </span>
                ))}
                <span className="fz-11 mt-2" style={{ color: '#6b7280' }}>
                  {formatSummaryDateWithSeconds(historyCart.created_at)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      {hasNextPage && carts.length > 0 && (
        <div className="tw-flex tw-justify-center tw-mt-4 tw-mb-2 tw-flex-shrink-0">
          <ButtonComponent
            outlined
            size="xs"
            variant="SECONDARY"
            className="tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-px-2 tw-py-1"
            iconClassName="!tw-w-4 !tw-h-4 tw-flex-shrink-0 tw-text-blue-500"
            text={loading ? t('InboxMC:event.loading') : t('InboxMC:event.showMore')}
            icon="arrow-ios-downward-outline"
            onClick={loadMoreCarts}
            disabled={loading}
          />
          </div>
        )}
    </div>
  );
};

export default CartHistory;
